import {
  ArrowRight,
  BadgeCheck,
  Check,
  Clock3,
  Globe2,
  Languages,
  Mail,
  MapPin,
  Menu,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import "./livinglink.css";

type Language = "en" | "de";
type BillingCycle = "monthly" | "annual";

type BilingualText = {
  en: string;
  de: string;
};

const copy = {
  en: {
    skip: "Skip to content",
    navigation: "Main navigation",
    home: "Home",
    property: "Property",
    services: "Services",
    areas: "Areas",
    events: "Events",
    pricing: "Pricing",
    listBusiness: "List your business",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    switchToEnglish: "Switch to English",
    switchToGerman: "Switch to German",
    language: "Language",
    heroEyebrow: "The Algarve · English & German",
    heroTitle: "Your life in the Algarve,",
    heroAccent: "connected.",
    heroBody:
      "Property, trusted services, local places and practical guides for residents, buyers and visitors.",
    explore: "Explore LivingLink",
    businessCta: "Reach an international Algarve audience",
    businessBody:
      "A bilingual profile puts your business in front of English- and German-speaking residents and visitors — and enquiries that reach you directly.",
    seePlans: "See the plans",
    proofOne: "English + German",
    proofOneNote: "one profile, two audiences",
    proofTwo: "Live within 24h",
    proofTwoNote: "after content approval",
    proofThree: "Direct enquiries",
    proofThreeNote: "straight to your own inbox",
    businessHeroEyebrow: "For Algarve businesses",
    businessHeroTitle: "Be found by the people",
    businessHeroAccent: "already here.",
    businessHeroBody:
      "Your verified LivingLink profile reaches English- and German-speaking residents, buyers and visitors — with enquiries that arrive in your own inbox.",
    businessPrimaryCta: "View plans",
    businessSecondaryCta: "Ask a question",
    businessProofEyebrow: "Clear from day one",
    businessProofTitle: "One profile. Two languages. Direct enquiries.",
    businessProofBody:
      "Send your business details once. We prepare the English and German versions, publish your profile after approval and show you how it performs.",
    businessStatOne: "EN + DE",
    businessStatOneNote: "included in every plan",
    businessStatTwo: "24 hours",
    businessStatTwoNote: "target after approval",
    businessStatThree: "Monthly",
    businessStatThreeNote: "cancel any month",
    pricingEyebrow: "For Algarve businesses",
    pricingTitle: "Simple plans. Clear value.",
    pricingAccent: "Always bilingual.",
    pricingIntro:
      "Choose the visibility that fits your business. Every plan includes an English and German profile and enquiries that reach you directly.",
    monthly: "Monthly",
    annual: "Annual",
    billingCycle: "Billing cycle",
    annualSaving: "2 months free",
    cancelNote: "Cancel monthly · Prices include the LivingLink service",
    annualNote: "Billed annually · Two months included at no extra cost",
    perMonth: "month",
    perYear: "year",
    recommended: "Recommended",
    starterTagline: "A trusted bilingual presence.",
    professionalTagline: "More visibility where it matters.",
    premiumTagline: "Maximum reach across LivingLink.",
    starterCta: "Choose Starter",
    professionalCta: "Choose Professional",
    premiumCta: "Choose Premium",
    allPlansTitle: "Included in every plan",
    allPlansBody:
      "No translation hand-off and no hidden setup fee. We prepare your profile in both languages and send enquiries directly to your business.",
    compareEyebrow: "Compare plans",
    compareTitle: "Choose by the visibility you need.",
    feature: "Feature",
    bilingualProfile: "Bilingual EN + DE profile",
    directEnquiries: "Direct customer enquiries",
    included: "Included",
    categoryPlacement: "Category placement",
    gallery: "Photo gallery",
    performance: "Performance report",
    featuredPlacement: "Featured placement",
    editorialSupport: "Editorial support",
    one: "1",
    upToThree: "Up to 3",
    allRelevant: "All relevant",
    fivePhotos: "5 photos",
    tenPhotos: "10 photos",
    twentyPhotos: "20 photos",
    standard: "Standard",
    priority: "Priority",
    dedicated: "Dedicated",
    faqEyebrow: "Good to know",
    faqTitle: "Straight answers before you join.",
    faqOneQ: "Are both languages really included?",
    faqOneA:
      "Yes. Your public profile is prepared in English and German. You send the source information once; LivingLink handles both audience versions.",
    faqTwoQ: "Where do enquiries go?",
    faqTwoA:
      "Straight to your business, by email or phone — whichever you list. LivingLink charges only the selected plan.",
    faqThreeQ: "How quickly can the listing go live?",
    faqThreeA:
      "A complete submission can normally go live within 24 hours after content and business details have been approved.",
    faqFourQ: "Can I change or cancel my plan?",
    faqFourA:
      "Yes. Monthly plans can be changed or cancelled monthly. Annual plans run for one year and include two months at no extra cost.",
    finalEyebrow: "Ready when you are",
    finalTitle: "Be found in both languages.",
    finalBody:
      "Tell us about your business once. We turn it into a clear English and German LivingLink profile.",
    startListing: "Start your listing",
    askQuestion: "Ask a question",
    footerBody:
      "The bilingual portal for property, services and local life in the Algarve.",
    discover: "Discover",
    company: "Company",
    about: "About",
    contact: "Contact",
    legal: "Legal",
    privacy: "Privacy",
    terms: "Terms",
    imprint: "Imprint",
    rights: "All rights reserved.",
  },
  de: {
    skip: "Zum Inhalt springen",
    navigation: "Hauptnavigation",
    home: "Startseite",
    property: "Immobilien",
    services: "Services",
    areas: "Regionen",
    events: "Events",
    pricing: "Preise",
    listBusiness: "Unternehmen eintragen",
    openMenu: "Menü öffnen",
    closeMenu: "Menü schließen",
    switchToEnglish: "Zu Englisch wechseln",
    switchToGerman: "Zu Deutsch wechseln",
    language: "Sprache",
    heroEyebrow: "Die Algarve · Englisch & Deutsch",
    heroTitle: "Dein Leben an der Algarve,",
    heroAccent: "verbunden.",
    heroBody:
      "Immobilien, vertrauenswürdige Services, lokale Orte und praktische Guides für Bewohner, Käufer und Besucher.",
    explore: "LivingLink entdecken",
    businessCta: "Erreiche ein internationales Algarve-Publikum",
    businessBody:
      "Ein zweisprachiges Profil bringt dein Unternehmen zu englisch- und deutschsprachigen Bewohnern und Besuchern — und zu Anfragen, die dich direkt erreichen.",
    seePlans: "Pläne ansehen",
    proofOne: "Englisch + Deutsch",
    proofOneNote: "ein Profil, zwei Zielgruppen",
    proofTwo: "In 24 Std. live",
    proofTwoNote: "nach Freigabe der Inhalte",
    proofThree: "Direkte Anfragen",
    proofThreeNote: "direkt in dein Postfach",
    businessHeroEyebrow: "Für Unternehmen an der Algarve",
    businessHeroTitle: "Werde von den Menschen gefunden,",
    businessHeroAccent: "die schon hier sind.",
    businessHeroBody:
      "Dein geprüftes LivingLink-Profil erreicht englisch- und deutschsprachige Bewohner, Käufer und Besucher — mit Anfragen, die in deinem Postfach landen.",
    businessPrimaryCta: "Pläne ansehen",
    businessSecondaryCta: "Frage stellen",
    businessProofEyebrow: "Vom ersten Tag an klar",
    businessProofTitle: "Ein Profil. Zwei Sprachen. Direkte Anfragen.",
    businessProofBody:
      "Übermittle deine Unternehmensdaten einmal. Wir erstellen die englische und deutsche Fassung, veröffentlichen dein Profil nach Freigabe und zeigen dir die Ergebnisse.",
    businessStatOne: "EN + DE",
    businessStatOneNote: "in jedem Plan enthalten",
    businessStatTwo: "24 Stunden",
    businessStatTwoNote: "Ziel nach der Freigabe",
    businessStatThree: "Monatlich",
    businessStatThreeNote: "monatlich kündbar",
    pricingEyebrow: "Für Unternehmen an der Algarve",
    pricingTitle: "Einfache Pläne. Klarer Wert.",
    pricingAccent: "Immer zweisprachig.",
    pricingIntro:
      "Wähle die Sichtbarkeit, die zu deinem Unternehmen passt. Jeder Plan enthält ein englisches und deutsches Profil und Anfragen, die dich direkt erreichen.",
    monthly: "Monatlich",
    annual: "Jährlich",
    billingCycle: "Abrechnungszeitraum",
    annualSaving: "2 Monate gratis",
    cancelNote: "Monatlich kündbar · Preise inklusive LivingLink-Service",
    annualNote: "Jährliche Abrechnung · Zwei Monate ohne Aufpreis enthalten",
    perMonth: "Monat",
    perYear: "Jahr",
    recommended: "Empfohlen",
    starterTagline: "Eine vertrauenswürdige zweisprachige Präsenz.",
    professionalTagline: "Mehr Sichtbarkeit an den richtigen Stellen.",
    premiumTagline: "Maximale Reichweite auf LivingLink.",
    starterCta: "Starter wählen",
    professionalCta: "Professional wählen",
    premiumCta: "Premium wählen",
    allPlansTitle: "In jedem Plan enthalten",
    allPlansBody:
      "Keine Übersetzungsübergabe und keine versteckte Einrichtungsgebühr. Wir erstellen dein Profil in beiden Sprachen und leiten Anfragen direkt an dein Unternehmen.",
    compareEyebrow: "Pläne vergleichen",
    compareTitle: "Wähle nach der Sichtbarkeit, die du brauchst.",
    feature: "Leistung",
    bilingualProfile: "Zweisprachiges EN- + DE-Profil",
    directEnquiries: "Direkte Kundenanfragen",
    included: "Enthalten",
    categoryPlacement: "Platzierung in Kategorien",
    gallery: "Fotogalerie",
    performance: "Performance-Report",
    featuredPlacement: "Hervorgehobene Platzierung",
    editorialSupport: "Redaktionelle Unterstützung",
    one: "1",
    upToThree: "Bis zu 3",
    allRelevant: "Alle relevanten",
    fivePhotos: "5 Fotos",
    tenPhotos: "10 Fotos",
    twentyPhotos: "20 Fotos",
    standard: "Standard",
    priority: "Priorität",
    dedicated: "Persönlich",
    faqEyebrow: "Gut zu wissen",
    faqTitle: "Klare Antworten, bevor du startest.",
    faqOneQ: "Sind beide Sprachen wirklich inklusive?",
    faqOneA:
      "Ja. Dein öffentliches Profil wird auf Englisch und Deutsch erstellt. Du übermittelst die Informationen einmal; LivingLink übernimmt beide Zielgruppenfassungen.",
    faqTwoQ: "Wohin gehen die Anfragen?",
    faqTwoA:
      "Direkt an dein Unternehmen — per E-Mail oder Telefon, je nachdem, was du hinterlegst. LivingLink berechnet ausschließlich den gewählten Plan.",
    faqThreeQ: "Wie schnell kann der Eintrag live sein?",
    faqThreeA:
      "Eine vollständige Einreichung kann normalerweise innerhalb von 24 Stunden nach Freigabe der Inhalte und Unternehmensdaten live gehen.",
    faqFourQ: "Kann ich den Plan ändern oder kündigen?",
    faqFourA:
      "Ja. Monatspläne können monatlich geändert oder gekündigt werden. Jahrespläne laufen ein Jahr und enthalten zwei Monate ohne Aufpreis.",
    finalEyebrow: "Bereit, wenn du es bist",
    finalTitle: "Werde in beiden Sprachen gefunden.",
    finalBody:
      "Erzähl uns einmal von deinem Unternehmen. Wir machen daraus ein klares englisches und deutsches LivingLink-Profil.",
    startListing: "Eintrag starten",
    askQuestion: "Frage stellen",
    footerBody:
      "Das zweisprachige Portal für Immobilien, Services und lokales Leben an der Algarve.",
    discover: "Entdecken",
    company: "Unternehmen",
    about: "Über uns",
    contact: "Kontakt",
    legal: "Rechtliches",
    privacy: "Datenschutz",
    terms: "AGB",
    imprint: "Impressum",
    rights: "Alle Rechte vorbehalten.",
  },
} as const;

const planFeatures: Record<"starter" | "professional" | "premium", BilingualText[]> = {
  starter: [
    { en: "English + German business profile", de: "Unternehmensprofil auf Englisch + Deutsch" },
    { en: "One category and Algarve area", de: "Eine Kategorie und Algarve-Region" },
    { en: "Direct email enquiries", de: "Direkte E-Mail-Anfragen" },
    { en: "Five gallery photos", de: "Fünf Galeriebilder" },
    { en: "Monthly performance snapshot", de: "Monatlicher Performance-Überblick" },
  ],
  professional: [
    { en: "Everything in Starter", de: "Alles aus Starter" },
    { en: "Featured category placement", de: "Hervorgehobene Kategorieplatzierung" },
    { en: "Up to three categories or areas", de: "Bis zu drei Kategorien oder Regionen" },
    { en: "Ten gallery photos", de: "Zehn Galeriebilder" },
    { en: "Priority profile updates", de: "Priorisierte Profil-Updates" },
  ],
  premium: [
    { en: "Everything in Professional", de: "Alles aus Professional" },
    { en: "Premium placement across LivingLink", de: "Premium-Platzierung auf LivingLink" },
    { en: "All relevant categories and areas", de: "Alle relevanten Kategorien und Regionen" },
    { en: "Twenty gallery photos", de: "Zwanzig Galeriebilder" },
    { en: "Dedicated editorial support", de: "Persönliche redaktionelle Betreuung" },
  ],
};

const plans = [
  {
    id: "starter",
    name: "Starter",
    price: 19,
    annualPrice: 190,
    checkoutUrl: "https://buy.stripe.com/00wdR91hRdXtagQa2tejK0q",
    annualCheckoutUrl: "https://buy.stripe.com/cNifZh3pZ6v11KkfmNejK0r",
    tagline: "starterTagline",
    cta: "starterCta",
    featured: false,
  },
  {
    id: "professional",
    name: "Professional",
    price: 39,
    annualPrice: 390,
    checkoutUrl: "https://buy.stripe.com/4gM6oH9On6v19cM3E5ejK0s",
    annualCheckoutUrl: "https://buy.stripe.com/00weVdbWvbPl3Ss0rTejK0t",
    tagline: "professionalTagline",
    cta: "professionalCta",
    featured: true,
  },
  {
    id: "premium",
    name: "Premium",
    price: 59,
    annualPrice: 590,
    checkoutUrl: "https://buy.stripe.com/5kQ3cv7GfaLhgFe4I9ejK0u",
    annualCheckoutUrl: "https://buy.stripe.com/aFaaEXe4D6v160AcaBejK0v",
    tagline: "premiumTagline",
    cta: "premiumCta",
    featured: false,
  },
] as const;

type CopyKey = keyof (typeof copy)["en"];

function BrandMark({ inverse = false }: { inverse?: boolean }) {
  return (
    <img
      className="brand-image"
      src={
        inverse
          ? "/assets/logo-primary-white.png"
          : "/assets/logo-primary-dark.png"
      }
      alt=""
      aria-hidden="true"
    />
  );
}

function Header({
  language,
  setLanguage,
}: {
  language: Language;
  setLanguage: (language: Language) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const t = copy[language];

  const navItems: Array<{ href: string; label: CopyKey }> = [
    { href: "/property", label: "property" },
    { href: "/services", label: "services" },
    { href: "/areas", label: "areas" },
    { href: "/events", label: "events" },
    { href: "/pricing", label: "pricing" },
  ];

  return (
    <header className="site-header">
      <div className="header-inner">
        <a className="logo" href="/" aria-label={`LivingLink Algarve — ${t.home}`}>
          <BrandMark />
          <span className="logo-type">
            <span>LivingLink</span>
            <small>Algarve</small>
          </span>
        </a>

        <nav className="desktop-nav" aria-label={t.navigation}>
          {navItems.map((item) => (
            <a
              href={item.href}
              key={item.href}
              aria-current={
                window.location.pathname.startsWith(item.href)
                  ? "page"
                  : undefined
              }
            >
              {t[item.label]}
            </a>
          ))}
        </nav>

        <div className="header-actions">
          <div
            className="language-switcher"
            role="group"
            aria-label={t.language}
          >
            <Languages size={14} aria-hidden="true" />
            <button
              type="button"
              aria-label={t.switchToEnglish}
              aria-pressed={language === "en"}
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
            <span aria-hidden="true">/</span>
            <button
              type="button"
              aria-label={t.switchToGerman}
              aria-pressed={language === "de"}
              onClick={() => setLanguage("de")}
            >
              DE
            </button>
          </div>
          <a className="header-cta" href="/list-your-business.html">
            {t.listBusiness}
            <ArrowRight size={14} aria-hidden="true" />
          </a>
          <button
            className="menu-button"
            type="button"
            aria-label={menuOpen ? t.closeMenu : t.openMenu}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((current) => !current)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="mobile-nav" aria-label={t.navigation}>
          {navItems.map((item) => (
            <a href={item.href} key={item.href}>
              {t[item.label]}
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          ))}
          <a className="mobile-cta" href="/list-your-business.html">
            {t.listBusiness}
          </a>
        </nav>
      )}
    </header>
  );
}

function Footer({
  language,
  setLanguage,
}: {
  language: Language;
  setLanguage: (language: Language) => void;
}) {
  const t = copy[language];

  return (
    <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-brand">
          <a className="logo footer-logo" href="/">
            <BrandMark inverse />
            <span className="logo-type">
              <span>LivingLink</span>
              <small>Algarve</small>
            </span>
          </a>
          <p>{t.footerBody}</p>
          <div
            className="footer-language"
            role="group"
            aria-label={t.language}
          >
            <button
              type="button"
              aria-pressed={language === "en"}
              onClick={() => setLanguage("en")}
            >
              English
            </button>
            <button
              type="button"
              aria-pressed={language === "de"}
              onClick={() => setLanguage("de")}
            >
              Deutsch
            </button>
          </div>
        </div>
        <div className="footer-column">
          <h2>{t.discover}</h2>
          <a href="/property">{t.property}</a>
          <a href="/services">{t.services}</a>
          <a href="/areas">{t.areas}</a>
          <a href="/events">{t.events}</a>
        </div>
        <div className="footer-column">
          <h2>{t.company}</h2>
          <a href="/about">{t.about}</a>
          <a href="/pricing">{t.pricing}</a>
          <a href="/contact">{t.contact}</a>
          <a href="/list-your-business.html">{t.listBusiness}</a>
        </div>
        <div className="footer-column">
          <h2>{t.legal}</h2>
          <a href="/privacy">{t.privacy}</a>
          <a href="/terms">{t.terms}</a>
          <a href="/impressum">{t.imprint}</a>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 LivingLink Algarve. {t.rights}</span>
        <span className="footer-location">
          <MapPin size={13} aria-hidden="true" />
          Algarve, Portugal
        </span>
      </div>
    </footer>
  );
}

function HomePage({ language }: { language: Language }) {
  const t = copy[language];

  return (
    <main id="main-content">
      <section className="home-hero">
        <div className="coast-line coast-line-one" aria-hidden="true" />
        <div className="coast-line coast-line-two" aria-hidden="true" />
        <div className="home-hero-inner">
          <p className="eyebrow light">{t.heroEyebrow}</p>
          <h1>
            {t.heroTitle}
            <em>{t.heroAccent}</em>
          </h1>
          <p className="hero-copy">{t.heroBody}</p>
          <div className="hero-actions">
            <a className="button button-light" href="/areas">
              {t.explore}
              <ArrowRight size={17} aria-hidden="true" />
            </a>
            <a className="text-link light-link" href="/pricing">
              {t.seePlans}
              <ArrowRight size={15} aria-hidden="true" />
            </a>
          </div>
        </div>
        <div className="hero-place-label" aria-hidden="true">
          <span>37.0179° N</span>
          <span>7.9304° W</span>
        </div>
      </section>

      <section className="home-business">
        <div className="section-heading">
          <p className="eyebrow">{t.listBusiness}</p>
          <h2>{t.businessCta}</h2>
          <p>{t.businessBody}</p>
          <a className="button button-dark" href="/pricing">
            {t.seePlans}
            <ArrowRight size={17} aria-hidden="true" />
          </a>
        </div>
        <div className="proof-grid">
          <article>
            <Globe2 size={21} aria-hidden="true" />
            <strong>{t.proofOne}</strong>
            <span>{t.proofOneNote}</span>
          </article>
          <article>
            <Clock3 size={21} aria-hidden="true" />
            <strong>{t.proofTwo}</strong>
            <span>{t.proofTwoNote}</span>
          </article>
          <article>
            <ShieldCheck size={21} aria-hidden="true" />
            <strong>{t.proofThree}</strong>
            <span>{t.proofThreeNote}</span>
          </article>
        </div>
      </section>

      <section className="final-cta home-final-cta">
        <div>
          <p className="eyebrow light">{t.businessHeroEyebrow}</p>
          <h2>{t.finalTitle}</h2>
          <p>{t.finalBody}</p>
        </div>
        <div className="final-actions">
          <a className="button button-accent" href="/list-your-business.html">
            {t.startListing}
            <ArrowRight size={17} aria-hidden="true" />
          </a>
          <a className="button button-ghost" href="/pricing">
            {t.businessPrimaryCta}
          </a>
        </div>
      </section>
    </main>
  );
}

function BillingSelector({
  billing,
  language,
  onChange,
}: {
  billing: BillingCycle;
  language: Language;
  onChange: (billing: BillingCycle) => void;
}) {
  const t = copy[language];

  return (
    <div className="billing-note">
      <div
        className="billing-toggle"
        role="group"
        aria-label={t.billingCycle}
      >
        <button
          type="button"
          aria-pressed={billing === "monthly"}
          onClick={() => onChange("monthly")}
        >
          {t.monthly}
        </button>
        <button
          type="button"
          aria-pressed={billing === "annual"}
          onClick={() => onChange("annual")}
        >
          {t.annual}
          <span>{t.annualSaving}</span>
        </button>
      </div>
      <p>{billing === "monthly" ? t.cancelNote : t.annualNote}</p>
    </div>
  );
}

function PlanCard({
  plan,
  language,
  billing,
}: {
  plan: (typeof plans)[number];
  language: Language;
  billing: BillingCycle;
}) {
  const t = copy[language];
  const price = billing === "annual" ? plan.annualPrice : plan.price;
  const period = billing === "annual" ? t.perYear : t.perMonth;
  const checkoutUrl =
    billing === "annual" ? plan.annualCheckoutUrl : plan.checkoutUrl;

  return (
    <article className={`plan-card ${plan.featured ? "featured" : ""}`}>
      {plan.featured && (
        <span className="recommended">
          <Sparkles size={13} aria-hidden="true" />
          {t.recommended}
        </span>
      )}
      <div className="plan-heading">
        <p className="plan-name">{plan.name}</p>
        <p className="plan-tagline">{t[plan.tagline]}</p>
      </div>
      <div
        className="price"
        aria-label={`€${price} / ${period}`}
      >
        <span>€</span>
        <strong>{price}</strong>
        <small>/ {period}</small>
      </div>
      <a
        className={`button ${plan.featured ? "button-accent" : "button-outline"}`}
        href={checkoutUrl}
      >
        {t[plan.cta]}
        <ArrowRight size={16} aria-hidden="true" />
      </a>
      <ul className="plan-features">
        {planFeatures[plan.id].map((feature) => (
          <li key={feature.en}>
            <span className="feature-check">
              <Check size={13} aria-hidden="true" />
            </span>
            {feature[language]}
          </li>
        ))}
      </ul>
    </article>
  );
}

function BusinessPage({ language }: { language: Language }) {
  const t = copy[language];
  const [billing, setBilling] = useState<BillingCycle>("monthly");

  return (
    <main id="main-content">
      <section className="business-hero">
        <div className="business-hero-glow" aria-hidden="true" />
        <div className="business-hero-inner">
          <div className="business-hero-copy">
            <p className="eyebrow light">{t.businessHeroEyebrow}</p>
            <h1>
              {t.businessHeroTitle}
              <em>{t.businessHeroAccent}</em>
            </h1>
            <p>{t.businessHeroBody}</p>
            <div className="hero-actions">
              <a className="button button-accent" href="#plans">
                {t.businessPrimaryCta}
                <ArrowRight size={17} aria-hidden="true" />
              </a>
              <a
                className="text-link light-link"
                href="mailto:hello@livinglinkalgarve.com"
              >
                {t.businessSecondaryCta}
                <ArrowRight size={15} aria-hidden="true" />
              </a>
            </div>
          </div>
          <aside className="business-hero-card" aria-label={t.businessProofTitle}>
            <p>{t.businessProofEyebrow}</p>
            <strong>{t.businessProofTitle}</strong>
            <span>{t.businessProofBody}</span>
          </aside>
        </div>
      </section>

      <section className="business-stats" aria-label={t.businessProofEyebrow}>
        <article>
          <strong>{t.businessStatOne}</strong>
          <span>{t.businessStatOneNote}</span>
        </article>
        <article>
          <strong>{t.businessStatTwo}</strong>
          <span>{t.businessStatTwoNote}</span>
        </article>
        <article>
          <strong>{t.businessStatThree}</strong>
          <span>{t.businessStatThreeNote}</span>
        </article>
      </section>

      <section className="plans-section business-plans" id="plans">
        <div className="section-heading centered">
          <p className="eyebrow">{t.pricingEyebrow}</p>
          <h2>{t.pricingTitle}</h2>
          <p>{t.pricingIntro}</p>
        </div>
        <BillingSelector
          billing={billing}
          language={language}
          onChange={setBilling}
        />
        <div className="plans-grid">
          {plans.map((plan) => (
            <PlanCard
              plan={plan}
              language={language}
              billing={billing}
              key={plan.id}
            />
          ))}
        </div>
      </section>

      <section className="final-cta">
        <div>
          <p className="eyebrow light">{t.finalEyebrow}</p>
          <h2>{t.finalTitle}</h2>
          <p>{t.finalBody}</p>
        </div>
        <div className="final-actions">
          <a className="button button-accent" href="#plans">
            {t.businessPrimaryCta}
            <ArrowRight size={17} aria-hidden="true" />
          </a>
          <a
            className="button button-ghost"
            href="mailto:hello@livinglinkalgarve.com"
          >
            <Mail size={16} aria-hidden="true" />
            {t.businessSecondaryCta}
          </a>
        </div>
      </section>
    </main>
  );
}

function CheckCell({ label }: { label: string }) {
  return (
    <span className="table-check" aria-label={label}>
      <Check size={14} aria-hidden="true" />
    </span>
  );
}

function ComparisonTable({ language }: { language: Language }) {
  const t = copy[language];

  return (
    <div
      className="comparison-wrap"
      role="region"
      aria-label={t.compareTitle}
      tabIndex={0}
    >
      <table>
        <thead>
          <tr>
            <th scope="col">{t.feature}</th>
            <th scope="col">Starter</th>
            <th scope="col">Professional</th>
            <th scope="col">Premium</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{t.bilingualProfile}</th>
            <td><CheckCell label={t.included} /></td>
            <td><CheckCell label={t.included} /></td>
            <td><CheckCell label={t.included} /></td>
          </tr>
          <tr>
            <th scope="row">{t.directEnquiries}</th>
            <td><CheckCell label={t.included} /></td>
            <td><CheckCell label={t.included} /></td>
            <td><CheckCell label={t.included} /></td>
          </tr>
          <tr>
            <th scope="row">{t.categoryPlacement}</th>
            <td>{t.one}</td>
            <td>{t.upToThree}</td>
            <td>{t.allRelevant}</td>
          </tr>
          <tr>
            <th scope="row">{t.gallery}</th>
            <td>{t.fivePhotos}</td>
            <td>{t.tenPhotos}</td>
            <td>{t.twentyPhotos}</td>
          </tr>
          <tr>
            <th scope="row">{t.performance}</th>
            <td><CheckCell label={t.included} /></td>
            <td><CheckCell label={t.included} /></td>
            <td><CheckCell label={t.included} /></td>
          </tr>
          <tr>
            <th scope="row">{t.featuredPlacement}</th>
            <td>—</td>
            <td><CheckCell label={t.included} /></td>
            <td><CheckCell label={t.included} /></td>
          </tr>
          <tr>
            <th scope="row">{t.editorialSupport}</th>
            <td>{t.standard}</td>
            <td>{t.priority}</td>
            <td>{t.dedicated}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PricingPage({ language }: { language: Language }) {
  const t = copy[language];
  const [billing, setBilling] = useState<BillingCycle>("monthly");
  const faqs = [
    [t.faqOneQ, t.faqOneA],
    [t.faqTwoQ, t.faqTwoA],
    [t.faqThreeQ, t.faqThreeA],
    [t.faqFourQ, t.faqFourA],
  ];

  return (
    <main id="main-content">
      <section className="pricing-hero">
        <div className="pricing-hero-copy">
          <p className="eyebrow light">{t.pricingEyebrow}</p>
          <h1>
            {t.pricingTitle}
            <em>{t.pricingAccent}</em>
          </h1>
          <p>{t.pricingIntro}</p>
        </div>
        <div className="pricing-proof" aria-label={t.allPlansTitle}>
          <span><Globe2 size={16} /> EN + DE</span>
          <span><Clock3 size={16} /> 24h</span>
          <span><ShieldCheck size={16} /> 0%</span>
        </div>
      </section>

      <section className="plans-section">
        <BillingSelector
          billing={billing}
          language={language}
          onChange={setBilling}
        />
        <div className="plans-grid">
          {plans.map((plan) => (
            <PlanCard
              plan={plan}
              language={language}
              billing={billing}
              key={plan.id}
            />
          ))}
        </div>
        <div className="all-plans-note">
          <BadgeCheck size={28} aria-hidden="true" />
          <div>
            <h2>{t.allPlansTitle}</h2>
            <p>{t.allPlansBody}</p>
          </div>
        </div>
      </section>

      <section className="comparison-section">
        <div className="section-heading centered">
          <p className="eyebrow">{t.compareEyebrow}</p>
          <h2>{t.compareTitle}</h2>
        </div>
        <ComparisonTable language={language} />
      </section>

      <section className="faq-section">
        <div className="faq-intro">
          <p className="eyebrow">{t.faqEyebrow}</p>
          <h2>{t.faqTitle}</h2>
        </div>
        <div className="faq-list">
          {faqs.map(([question, answer], index) => (
            <details key={question} open={index === 0}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="final-cta">
        <div>
          <p className="eyebrow light">{t.finalEyebrow}</p>
          <h2>{t.finalTitle}</h2>
          <p>{t.finalBody}</p>
        </div>
        <div className="final-actions">
          <a className="button button-accent" href="/list-your-business.html">
            {t.startListing}
            <ArrowRight size={17} aria-hidden="true" />
          </a>
          <a
            className="button button-ghost"
            href="mailto:hello@livinglinkalgarve.com"
          >
            <Mail size={16} aria-hidden="true" />
            {t.askQuestion}
          </a>
        </div>
      </section>
    </main>
  );
}

function initialLanguage(): Language {
  const requested = new URLSearchParams(window.location.search).get("lang");
  if (requested === "en" || requested === "de") return requested;
  const stored = window.localStorage.getItem("livinglink-language");
  if (stored === "en" || stored === "de") return stored;
  return navigator.language.toLowerCase().startsWith("de") ? "de" : "en";
}

export function LivingLinkApp() {
  const [language, setLanguage] = useState<Language>(initialLanguage);
  const isPricing = window.location.pathname.startsWith("/pricing");
  const isBusiness = window.location.pathname.startsWith("/list-your-business");

  useEffect(() => {
    const t = copy[language];
    document.documentElement.lang = language;
    document.title = isBusiness
      ? language === "de"
        ? "Unternehmen eintragen | LivingLink Algarve"
        : "List Your Business | LivingLink Algarve"
      : isPricing
      ? language === "de"
        ? "Preise — Unternehmen eintragen | LivingLink Algarve"
        : "Pricing — List Your Algarve Business | LivingLink Algarve"
      : language === "de"
        ? "LivingLink Algarve — Das zweisprachige Algarve-Portal"
        : "LivingLink Algarve — The bilingual Algarve portal";
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    if (description) {
      description.content = isBusiness
        ? t.businessHeroBody
        : isPricing
          ? t.pricingIntro
          : t.heroBody;
    }
    window.localStorage.setItem("livinglink-language", language);
    window.localStorage.setItem("ll_lang", language);
  }, [isBusiness, isPricing, language]);

  return (
    <div className="livinglink-app">
      <a className="skip-link" href="#main-content">
        {copy[language].skip}
      </a>
      <Header language={language} setLanguage={setLanguage} />
      {isBusiness ? (
        <BusinessPage language={language} />
      ) : isPricing ? (
        <PricingPage language={language} />
      ) : (
        <HomePage language={language} />
      )}
      <Footer language={language} setLanguage={setLanguage} />
    </div>
  );
}
